import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { SecureStorageService } from '../../../core/services/secure-storage.service';

/**
 * Why the loyalty gateway refused the caller.
 *   'expired'       → no usable token; signing in again fixes it
 *   'not-permitted' → token is fine, this account's role may never use the module
 *   'action'        → session is fine, this one operation was refused
 * The first two are module-wide; the third is not.
 */
export type LoyaltyRefusal = 'expired' | 'not-permitted' | 'action';

/**
 * Single place that decides what the user is told when the Loyalty
 * micro-service refuses a call.
 *
 * The gateway answers a caller it doesn't accept with its own wording —
 * "Authentication required" — which used to reach the user verbatim, as one
 * red toast per failed widget on every loyalty page. It reads like a bug and
 * tells the user nothing they can act on. Refusals now route through here, so
 * the user gets the /access-restricted screen once, worded for the actual
 * reason: an expired session says sign in again, a role that isn't allowed
 * says contact a Super Admin. Guessing between the two is what the gateway's
 * per-reason codes exist to prevent.
 */
@Injectable({ providedIn: 'root' })
export class LoyaltyAccessService {
  /** Names the module on the shared /access-restricted screen. */
  private static readonly MODULE = 'Loyalty';

  /**
   * Gateway refusal codes, from the backend auth guard
   * (backend-microservices `src/app.service.ts`, ADMIN scope).
   */
  // ERROR_CODE_ACCESS_DENIED is the gateway's code for a rejected session now — the legacy word,
  // adopted because the mobile app and client_latest both already branch on it. INVALID_TOKEN
  // stays in the list: it is still sent as `reason`, and dropping it would silently stop
  // recognising an expired session if anything still answers with it.
  private static readonly EXPIRED_CODES = [
    'UNAUTHENTICATED', 'ERROR_CODE_ACCESS_DENIED', 'INVALID_TOKEN', 'HTTP_401', 'TOKEN_REVOKED', 'TOKEN_EXPIRED',
  ];
  private static readonly NOT_PERMITTED_CODES = ['ADMIN_REQUIRED', 'SUPER_ADMIN_REQUIRED'];
  private static readonly ACTION_CODES = ['FORBIDDEN', 'HTTP_403'];

  /** No usable loyalty session — for callers that can't redirect (see denySession). */
  static readonly EXPIRED_MESSAGE =
    'Your Loyalty session has expired. Please sign in again to continue.';

  /** Signed in fine, but this account may not use the module at all. */
  static readonly NOT_PERMITTED_MESSAGE =
    "Your account doesn't have access to the Loyalty module. It is limited to " +
    'Super Admin accounts — please contact a Super Admin to request access.';

  /** A single loyalty action was refused while the session itself is still valid. */
  static readonly ACTION_FORBIDDEN_MESSAGE =
    "You don't have permission to perform this action. Please contact a Super " +
    'Admin if you need it enabled for your account.';

  /** Carried on the errors this service produces — loyalty components use the
   *  code as the toast title, so it has to be readable, not a machine code. */
  private static readonly ERROR_CODE = 'Access Restricted';

  /**
   * One loyalty page fires several gateway calls at once (grid + dropdowns +
   * KPI cards). Without a window around the redirect, every one of them would
   * queue its own navigation — the pile-up this service exists to remove.
   */
  private static readonly REDIRECT_WINDOW_MS = 5000;

  private lastRedirectAt = 0;

  constructor(
    private router: Router,
    private secureStorage: SecureStorageService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /** The screen that explains the restriction. Guards can return this directly. */
  restrictedUrl(reason: 'expired' | 'not-permitted' = 'not-permitted'): UrlTree {
    return this.router.createUrlTree(['/access-restricted'], {
      queryParams: { module: LoyaltyAccessService.MODULE, reason },
    });
  }

  /**
   * Terminal observable for a call the gateway won't accept.
   *
   * Inside the loyalty module the user is moved to the access-restricted screen
   * — worded for `reason` — and the per-call error is swallowed, so the
   * component-level toasts don't shout the same thing once per widget.
   *
   * Outside the module (Users, Add Contest — loyalty data is optional garnish
   * there) it stays an ordinary error: those callers fall back to their
   * non-loyalty data on failure and must keep receiving it.
   */
  denySession(reason: 'expired' | 'not-permitted'): Observable<never> {
    if (isPlatformBrowser(this.platformId) && this.isInLoyaltyModule()) {
      this.redirectOnce(reason);
      return EMPTY;
    }
    return throwError(() => this.refusalError(reason));
  }

  /** Error object for a refusal — reads as a permission problem, not a crash. */
  refusalError(refusal: LoyaltyRefusal): { message: string; code: string } {
    return {
      message:
        refusal === 'expired' ? LoyaltyAccessService.EXPIRED_MESSAGE
          : refusal === 'not-permitted' ? LoyaltyAccessService.NOT_PERMITTED_MESSAGE
            : LoyaltyAccessService.ACTION_FORBIDDEN_MESSAGE,
      code: LoyaltyAccessService.ERROR_CODE,
    };
  }

  /**
   * Which refusal is this — if it is one at all (null = a genuine request error)?
   *
   * The `code` is the contract and is matched exactly; the gateway sends a
   * distinct one per reason:
   *   { message: 'Authentication required',          code: 'UNAUTHENTICATED' }
   *   { message: 'Invalid or expired token',         code: 'INVALID_TOKEN' }
   *   { message: 'Admin access required',            code: 'ADMIN_REQUIRED' }
   *   { message: 'Access denied — super admin only', code: 'SUPER_ADMIN_REQUIRED' }
   *   { message: 'Forbidden (internal key required)', code: 'FORBIDDEN' }
   * All of them arrive in the body of a 201 — the gateway does not use HTTP
   * status for refusals — so `HTTP_401`/`HTTP_403` only show up behind a proxy.
   *
   * The message-text pass below is a fallback, not the contract: the deployed
   * gateway still answers both role denials with a blanket `FORBIDDEN`, which
   * on its own would be read as merely action-scoped.
   */
  classifyRefusal(message?: string | null, code?: string | null): LoyaltyRefusal | null {
    const rawCode = (code || '').trim().toUpperCase();
    if (LoyaltyAccessService.EXPIRED_CODES.includes(rawCode)) return 'expired';
    if (LoyaltyAccessService.NOT_PERMITTED_CODES.includes(rawCode)) return 'not-permitted';

    const text = `${message || ''} ${code || ''}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    if (/unauthenticat|authentication|unauthoriz|invalid token|expired token|token expired|token revoked|token_revoked|session has ended|missing token|no token|not logged in|no session/.test(text)) {
      return 'expired';
    }
    // Role denial is module-wide, so it must be caught before the generic
    // FORBIDDEN below — otherwise it reads as one refused call and the user
    // gets a toast per widget instead of one explanation.
    if (/admin required|admin access required|super admin/.test(text)) {
      return 'not-permitted';
    }
    if (LoyaltyAccessService.ACTION_CODES.includes(rawCode) ||
      /forbidden|permission denied|not permitted|not allowed|insufficient (permission|privilege|role)/.test(text)) {
      return 'action';
    }
    return null;
  }

  private isInLoyaltyModule(): boolean {
    return this.router.url.startsWith('/loyalty');
  }

  private redirectOnce(reason: 'expired' | 'not-permitted'): void {
    const now = Date.now();
    if (now - this.lastRedirectAt < LoyaltyAccessService.REDIRECT_WINDOW_MS) return;
    this.lastRedirectAt = now;

    // The stored token isn't accepted by the gateway, so drop it: the sidebar
    // link hides itself and LoyaltyAccessGuard turns the user away at the door
    // from now on, instead of letting them walk into another page of failures.
    this.secureStorage.removeItem('msAuthToken');
    if (reason === 'expired') {
      this.secureStorage.removeItem('authToken');
    }
    this.router.navigateByUrl(this.restrictedUrl(reason));
  }
}
